import { sharedMarginRuleLabel } from './SharedRuleEquivalents';
import { FormatCompletionNotice } from './FormatCompletionNotice';
import { resolveTemporaryUnitPrice } from '../../utils/temporaryPriceCore';
import { packWeightLabel } from '../../utils/varietyFormatIdentity';
import QuickPriceWarningIndicator from './QuickPriceWarningIndicator';
import {
  getQuickAvailabilityCompletion,
  getQuickAvailabilityWarnings,
  getQuickFormatCompletion,
  getQuickPriceWarnings,
  summarizeQuickPriceWarnings,
} from './quickPriceWarnings';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiChevronRight,
  FiPlus,
  FiRefreshCw,
} from 'react-icons/fi';
import { TbLock, TbLockOpen2 } from 'react-icons/tb';
import InlineQuickPriceInput from './InlineQuickPriceInput';
import InfoTooltip from '../InfoTooltip';
import InlineQuickPriceRuleInput from './InlineQuickPriceRuleInput';
import QuickPriceRuleEquivalent from './QuickPriceRuleEquivalent';
import QuickPriceRuleBadge, { useUniformRuleBadgeWidth } from './QuickPriceRuleBadge';
import useStickyPriceTableHeader from './useStickyPriceTableHeader';
import { getQuickRuleEquivalent, getQuickRuleReferenceCost, getQuickRuleMargin, formatMarginRuleLabel } from './priceRuleEquivalence';
import QuickPromotionRows from './QuickPromotionRows';
import QuickOfferSummary from './QuickOfferSummary';
import BranchOfferRows from './BranchOfferRows';
import { isOfferFormat } from './OfferAvailabilityDialog';
import { getQuickPriceRowStyle, getSharedPriceRule } from './quickPriceGroupPresentation';
import {
  formatActiveRule,
  formatMoney,
  getAvailabilitySummary,
  getActiveManualCost,
  getConfiguredFormats,
  getCurrentProduct,
  getEditableStandardStockCostSource,
  getEffectiveCost,
  hasStockPurchaseSource,
  getPriceLockSummary,
  getProductFormats,
  isSelectableQuickPriceFormat,
} from './quickPriceListModel';
import {
  findComplexPriceRule,
  formatComplexRangeLabel,
  hasPriceRuleSelection,
  normalizePriceRuleSelection,
  resolvePriceRuleSelection,
} from '../../utils/complexPriceRules';
import { isPromotionCurrent, normalizeFormatPromotions } from '../../utils/priceListPromotions';

const FALLBACK_IMAGE = 'https://placehold.co/80x80?text=Producto';

const availabilityToneClasses = {
  available: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  unavailable: 'border-red-200 bg-red-50 text-red-700',
  partial: 'border-amber-200 bg-amber-50 text-amber-800',
  neutral: 'border-slate-200 bg-slate-100 text-slate-600',
};

const availabilityBadgeClasses = 'inline-flex h-9 w-[7.5rem] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-2 text-xs font-bold';
const availabilityLayoutClasses = 'grid grid-cols-[7.5rem_1.75rem] items-center gap-1';

const getOfferPreviewPrice = (format) => {
  const analysis = format.analysis;
  const price = analysis?.stockPriceRule?.sellingPrice;
  return hasPriceRuleSelection(format.priceRule, format.stockPriceRule)
    && !analysis?.priceRule?.error && !analysis?.stockPriceRule?.error
    && Number.isSafeInteger(price) && price >= 0 ? price : null;
};

const SelectionCheckbox = ({
  checked = false,
  mixed = false,
  disabled = false,
  ariaLabel,
  title,
  onChange,
  className = '',
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = Boolean(mixed);
  }, [mixed]);

  return (
    <label
      title={title}
      className={`inline-flex min-h-9 min-w-9 shrink-0 cursor-pointer items-center justify-center rounded-md hover:bg-blue-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-500 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 ${className}`.trim()}
    >
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        aria-checked={mixed ? 'mixed' : checked ? 'true' : 'false'}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={onChange}
        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
      />
    </label>
  );
};

const getFormatSelectionState = (formats, selectedFormatIdSet) => {
  const selectableFormats = (Array.isArray(formats) ? formats : []).filter(
    isSelectableQuickPriceFormat
  );
  const selectedCount = selectableFormats.filter(
    (format) => selectedFormatIdSet.has(String(format.id))
  ).length;
  return {
    checked: selectableFormats.length > 0 && selectedCount === selectableFormats.length,
    mixed: selectedCount > 0 && selectedCount < selectableFormats.length,
  };
};

const priceLockToneClasses = {
  locked: 'border-red-700 bg-red-600 text-white hover:bg-red-700',
  partial: 'border-yellow-500 bg-yellow-400 text-slate-950 hover:bg-yellow-500',
  unlocked: 'border-green-800 bg-green-700 text-white hover:bg-green-800',
};

const getPriceLockLabel = (summary, { grouped = false } = {}) => {
  if (summary.state === 'locked') return grouped ? 'Todos bloqueados' : 'Bloqueado';
  if (summary.state === 'partial') {
    return `${summary.lockedCount} de ${summary.totalCount} bloqueados`;
  }
  return grouped ? 'Sin bloqueos' : 'Desbloqueado';
};

const PriceLockControl = ({
  formats,
  subjectLabel,
  grouped = false,
  canUpdate = false,
  saving = false,
  disabled = false,
  onChange,
}) => {
  const summary = getPriceLockSummary(formats);
  if (!summary.totalCount) return null;
  const isLocked = summary.state === 'locked';
  const nextLocked = !isLocked;
  const statusLabel = getPriceLockLabel(summary, { grouped });
  const actionLabel = nextLocked
    ? `Bloquear precio y regla de ${subjectLabel}`
    : `Desbloquear precio y regla de ${subjectLabel}`;
  const content = saving
    ? <FiRefreshCw aria-hidden="true" className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
    : isLocked || summary.state === 'partial'
      ? <TbLock aria-hidden="true" className="h-4 w-4" />
      : <TbLockOpen2 aria-hidden="true" className="h-4 w-4" />;
  const classes = `inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition ${priceLockToneClasses[summary.state]}`;

  if (!canUpdate) {
    return (
      <span aria-label={`${statusLabel}: ${subjectLabel}`} title={`${statusLabel}: ${subjectLabel}`} className={classes}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      aria-label={actionLabel}
      aria-pressed={isLocked}
      aria-busy={saving}
      title={`${saving ? 'Actualizando…' : statusLabel}. ${actionLabel}. El recálculo automático seguirá disponible.`}
      disabled={disabled || saving}
      onClick={() => onChange?.(nextLocked)}
      className={`${classes} disabled:cursor-not-allowed disabled:opacity-60`}
    >
      {content}
    </button>
  );
};

const AvailabilityButton = ({
  isAvailable,
  isConfigured,
  saving,
  disabled,
  label,
  onClick,
  ariaLabel,
}) => {
  const tone = !isConfigured ? 'neutral' : isAvailable ? 'available' : 'unavailable';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={isConfigured ? isAvailable : undefined}
      disabled={disabled || saving || !isConfigured}
      onClick={onClick}
      className={`${availabilityBadgeClasses} transition disabled:cursor-not-allowed disabled:opacity-60 ${availabilityToneClasses[tone]}`}
    >
      {saving ? 'Actualizando…' : label || (!isConfigured ? 'Sin configurar' : isAvailable ? 'Disponible' : 'No disponible')}
    </button>
  );
};

const BELOW_COST_DESCRIPTION = 'El precio final está por debajo del costo calculado.';

const isBelowEffectiveCost = (value, effectiveCost) => {
  if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return false;
  const price = Number(value);
  return Number.isInteger(price) && price >= 0
    && Number.isFinite(effectiveCost) && price < effectiveCost;
};

const getBasisCostForEstimatedEditor = (format) => {
  const candidates = [
    format?.analysis?.referenceCost?.basisValue,
    format?.costs?.calculatedCost,
  ];
  const value = candidates.find((candidate) => (
    candidate !== null
    && candidate !== undefined
    && String(candidate).trim() !== ''
    && Number.isFinite(Number(candidate))
    && Number(candidate) >= 0
  ));
  return value === undefined ? null : Math.round(Number(value));
};

const CostSummary = ({ format, detailsOnly = false }) => {
  const cost = getEffectiveCost(format);
  if (cost.effective === null) {
    if (detailsOnly) return null;
    const needsConversion = ['missing_unit_weight', 'missing_weight'].includes(cost.status);
    return (
      <div className="text-sm">
        <span className="font-semibold text-slate-500">Sin costo efectivo</span>
        <span className="mt-0.5 block text-xs text-amber-700">
          {needsConversion ? 'Falta peso o conversión' : 'Sin compra o costo manual'}
        </span>
      </div>
    );
  }
  const hasDetails = cost.base !== null || cost.operational !== null || cost.source;
  if (detailsOnly && !hasDetails) return null;
  return (
    <div className="text-sm">
      {!detailsOnly && (
        <span className="font-bold text-slate-900">{formatMoney(cost.effective)}</span>
      )}
      {(cost.base !== null || cost.operational !== null) && (
        <span className="mt-0.5 block text-xs text-slate-500">
          {cost.base !== null ? `${formatMoney(cost.base)} base` : 'Base sin dato'}
          {' + '}
          {cost.operational !== null ? `${formatMoney(cost.operational)} op.` : '$0 op.'}
        </span>
      )}
      {cost.source && <span className="block truncate text-[11px] text-slate-400">{cost.source}</span>}
    </div>
  );
};

const getFormatPriceRuleSelection = (format) => normalizePriceRuleSelection(
  format?.priceRule,
  format?.stockPriceRule
);

const SharedPriceRuleBadge = ({ formats, groupLabel, complexPriceRules = [] }) => {
  const selections = (Array.isArray(formats) ? formats : []).map(getFormatPriceRuleSelection);
  const firstSelection = selections[0];
  if (firstSelection?.type === 'complex'
    && selections.length > 0
    && selections.every((selection) => selection?.type === 'complex'
      && selection.complexPriceRuleId === firstSelection.complexPriceRuleId)) {
    const complexRule = findComplexPriceRule(
      complexPriceRules,
      firstSelection.complexPriceRuleId
    );
    const label = complexRule?.name || `Regla compleja #${firstSelection.complexPriceRuleId}`;
    return (
      <QuickPriceRuleBadge
        aria-label={`Regla compartida de ${groupLabel}: ${label}`}
        title={`Todos los formatos visibles: ${label} · ${sharedMarginRuleLabel(formats, complexPriceRules)}`}
        tone="violet"
      >
        {sharedMarginRuleLabel(formats, complexPriceRules)}
      </QuickPriceRuleBadge>
    );
  }
  const rule = getSharedPriceRule(formats);
  if (!rule) return null;
  const label = sharedMarginRuleLabel(formats, complexPriceRules);
  return (
    <QuickPriceRuleBadge
      aria-label={`Regla compartida de ${groupLabel}: ${label}`}
      title={`Todos los formatos visibles: ${label}`}
    >
      {label}
    </QuickPriceRuleBadge>
  );
};

const FormatRow = ({
  warnings = [],
  onDisplayedDraftChange,
  onCompleteFormat,
  onReviewPendingFormat,
  pendingOperations = [],
  product,
  variety,
  format,
  relatedOffers = [],
  hideOfferSummaries = false,
  canUpdate,
  canUpdateRegularPricing = canUpdate,
  showAvailability = true,
  canCorrectStockCosts,
  canCreateStock = false,
  hasVarietyStockSource = false,
  savingPrice,
  savingAvailability,
  productAvailabilitySaving,
  savingCost,
  savingRule,
  savingLock,
  recalculationReady,
  recalculationBlocked = false,
  recalculatingPrice,
  promotionNow,
  savingPromotion,
  promotionError,
  onCreatePromotion,
  onEditPromotion,
  onEndPromotion,
  onEditOffer,
  onEndOffer,
  offerBusy = {},
  offerErrors = {},
  promotionMutationDisabled = false,
  error,
  onSavePrice,
  onPriceDraftChange,
  onSaveCost,
  onEditCalculatedCost,
  onAddStockInput,
  onSaveRule,
  onCostDraftChange,
  onRuleDraftChange,
  onCostDirtyChange,
  onRuleDirtyChange,
  onPriceDirtyChange,
  onRecalculatePrice,
  onSetPriceLocks,
  onToggleAvailability,
  selectionEnabled = false,
  selected = false,
  onToggleSelection,
  priceResetSignal,
  costResetSignal,
  ruleResetSignal,
  selectionDisabled = false,
  costMutationDisabled = false,
  priceMutationDisabled = false,
  ruleMutationDisabled = false,
  availabilityMutationDisabled = false,
  lockMutationDisabled = false,
  isTarget = false,
  complexPriceRules = [],
}) => {
  const offerFormat = isOfferFormat(format);
  const canEditPricing = canUpdate && (canUpdateRegularPricing || offerFormat);
  const offerPreviewPrice = offerFormat ? getOfferPreviewPrice(format) : null;
  const [priceDraft, setPriceDraft] = useState(format.sellingPrice);
  useEffect(() => () => onDisplayedDraftChange(format.id, null), [format.id, onDisplayedDraftChange]);
  const currentPromotions = normalizeFormatPromotions(format)
    .filter((promotion) => isPromotionCurrent(promotion, promotionNow));
  const priceRuleSelection = getFormatPriceRuleSelection(format);
  const formatName = format.productFormatName
    || format.ProductFormat?.name
    || format.clientTypeLabel
    || 'Formato';
  const unitLabel = format.metricUnit || 'Unidad sin definir';
  const weight = Number(format.unitWeightKg);
  const effectiveCost = getEffectiveCost(format);
  const costBreakdown = format?.costs?.referenceCostBreakdown;
  const calculatedCost = format?.costs?.calculatedCost;
  const hasActiveStandardStockCost = (
    (format?.costs?.costSource || format?.costs?.referenceCostSource) === 'stock_list'
    && costBreakdown?.listType === 'standard'
  );
  const editableStandardStockCostSource = getEditableStandardStockCostSource(format?.costs);
  const hasEditableCalculatedSource = Boolean(editableStandardStockCostSource);
  const storedManualCost = getActiveManualCost(format);
  const estimatedEditorBasisCost = getBasisCostForEstimatedEditor(format);
  const manualEditorValue = storedManualCost ?? (
    hasActiveStandardStockCost && !hasEditableCalculatedSource
      && estimatedEditorBasisCost !== null
      ? estimatedEditorBasisCost
      : null
  );
  const belowCost = isBelowEffectiveCost(offerFormat ? offerPreviewPrice : canUpdate ? priceDraft : format.sellingPrice, effectiveCost.effective);
  const ruleBasis = priceRuleSelection?.type === 'simple'
    ? priceRuleSelection.rule?.basis || format.analysis?.pricingBasis || product.pricingBasis || 'unit'
    : format.analysis?.pricingBasis || product.pricingBasis || 'unit';
  const ruleReferenceCost = getQuickRuleReferenceCost(format, ruleBasis);
  const resolvedSelection = resolvePriceRuleSelection(priceRuleSelection, {
    complexPriceRules,
    referenceCost: ruleReferenceCost,
    basis: ruleBasis,
  });
  const analysisPriceRule = format.analysis?.priceRule || {};
  const complexRule = priceRuleSelection?.type === 'complex'
    ? resolvedSelection.complexPriceRule
      || analysisPriceRule.complexPriceRule
      || findComplexPriceRule(complexPriceRules, priceRuleSelection.complexPriceRuleId)
    : null;
  const matchedRange = priceRuleSelection?.type === 'complex'
    ? analysisPriceRule.matchedRange || resolvedSelection.matchedRange
    : null;
  const resolvedSimpleRule = priceRuleSelection?.type === 'complex'
    ? analysisPriceRule.resolvedRule || resolvedSelection.resolvedRule
    : priceRuleSelection?.rule || null;
  const ruleLabel = priceRuleSelection?.type === 'complex'
    ? `Regla compleja: ${complexRule?.name || `#${priceRuleSelection.complexPriceRuleId}`}`
    : formatActiveRule(resolvedSimpleRule);
  const accessibleFormatName = `${formatName}${packWeightLabel(format) ? ` ${unitLabel} ${packWeightLabel(format)}` : ''} de ${variety.name} de ${product.name}`;
  const costCorrectionDisabled = !canCorrectStockCosts
    || costMutationDisabled || recalculatingPrice || savingCost;
  const costCorrectionDisabledReason = !canCorrectStockCosts
    ? 'Necesitas el permiso stock.update para corregir este costo.'
    : costMutationDisabled
      ? 'La edición de costos está bloqueada mientras se completa otra operación.'
      : recalculatingPrice
        ? 'Espera a que termine el recálculo del precio.'
        : savingCost
          ? 'Espera a que termine el guardado del costo.'
          : '';
  const costCorrectionDescriptionId = `quick-cost-correction-help-${format.id}`;
  const recalculationActionLabel = `Recalcular y aplicar el precio de ${accessibleFormatName}`;
  const showRecalculationAction = canUpdateRegularPricing && !offerFormat && recalculationReady && format.isConfigured
    && hasPriceRuleSelection(priceRuleSelection);
  const recalculationActionDisabled = recalculationBlocked || recalculatingPrice || savingPrice || savingCost || savingRule
    || productAvailabilitySaving || priceMutationDisabled
    || costMutationDisabled || ruleMutationDisabled;
  const priceLocked = Boolean(format.isPriceLocked);
  const stockCreationAction = canCreateStock && !hasVarietyStockSource && !hasStockPurchaseSource(format.costs) ? (
    <button type="button"
      aria-label={`Agregar ingreso de stock de ${accessibleFormatName}`}
      title="Agregar ingreso de stock"
      disabled={costMutationDisabled || savingCost || recalculatingPrice}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onAddStockInput?.({ product, variety, format })}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50">
      <FiPlus aria-hidden="true" className="h-4 w-4" />
    </button>
  ) : null;
  const promotionBlockedReason = !format.isConfigured
    ? 'Configura primero el precio normal de esta sucursal para agregar un precio temporal.'
    : format.completion?.isDraft
      ? 'Completa los datos del formato antes de agregar un precio temporal.'
      : '';
  const promotionBusy = promotionMutationDisabled || recalculatingPrice || savingPromotion;
  const activeTemporaryPrice = resolveTemporaryUnitPrice(format.sellingPrice, currentPromotions, promotionNow);
  const promotionActionText = currentPromotions.length ? '+ Agregar otro precio temporal' : '+ Agregar precio temporal';
  const promotionActionClassName = 'min-h-8 rounded px-1 text-xs font-semibold text-amber-800 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-60';

  return (
    <React.Fragment>
      <tr
        id={`quick-format-${format.id}`}
        tabIndex={isTarget ? -1 : undefined}
        aria-label={isTarget ? `Formato solicitado: ${formatName}` : undefined}
        aria-selected={selectionEnabled ? selected : undefined}
        data-target={isTarget || undefined}
        className="quick-price-row quick-price-format"
        style={getQuickPriceRowStyle('format', selected)}
      >
        <th
          scope="row"
          className="sticky left-0 z-10 py-2 pl-10 pr-3 text-left align-top font-normal"
        >
          <div className="flex min-w-0 items-start gap-2">
            {selectionEnabled && (
              <SelectionCheckbox
                checked={selected}
                disabled={selectionDisabled || (canEditPricing && priceLocked)}
                ariaLabel={`Seleccionar formato ${accessibleFormatName}`}
                title={canEditPricing && priceLocked
                  ? 'Desbloquea este formato con el candado antes de seleccionarlo.'
                  : undefined}
                onChange={(event) => onToggleSelection?.(event.target.checked)}
                className="mt-0.5"
              />
            )}
            <div className="min-w-0 flex-1">
              <span className="quick-price-level">Formato</span>
              <div className="flex min-w-0 items-center gap-1.5">
                <p className="truncate text-sm font-bold text-slate-900">{formatName}</p>
                <FormatCompletionNotice compact completion={getQuickFormatCompletion(format)} pendingCount={pendingOperations.length}
                  onComplete={pendingOperations.length && onReviewPendingFormat
                    ? () => onReviewPendingFormat({ product, variety, format, operations: pendingOperations })
                    : onCompleteFormat ? () => onCompleteFormat({ product, variety, format }) : undefined} />
                <QuickPriceWarningIndicator messages={warnings} subject={accessibleFormatName} />
              </div>
              <p className="truncate text-[10px] text-slate-500" title={`${product.name} / ${variety.name}`}>
                {product.name} / {variety.name}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {unitLabel}{Number.isFinite(weight) && weight > 0 ? ` · ${weight} kg/unidad` : ''}
              </p>
              {format.barcode && <p className="mt-0.5 truncate text-[11px] text-slate-400">Código {format.barcode}</p>}
              <div className="mt-1">
                <PriceLockControl
                  formats={[format]}
                  subjectLabel={accessibleFormatName}
                  canUpdate={canEditPricing}
                  saving={savingLock}
                  disabled={lockMutationDisabled}
                  onChange={(isLocked) => onSetPriceLocks?.({
                    product,
                    variety,
                    formats: [format],
                    isLocked,
                  })}
                />
              </div>
            </div>
          </div>
        </th>

      <td className="px-3 py-2 align-top">
        {canUpdateRegularPricing && !offerFormat ? (
          hasEditableCalculatedSource ? (
            <div>
              <InlineQuickPriceInput
                formatId={format.id}
                inputIdPrefix="quick-cost"
                ariaLabel={`Costo calculado de ${accessibleFormatName}`}
                fieldLabel="costo"
                requireEditActivation
                editButtonAriaLabel={`Corregir costo calculado de ${accessibleFormatName}`}
                editButtonAriaDescribedBy={costCorrectionDisabledReason
                  ? costCorrectionDescriptionId
                  : undefined}
                editButtonTitle={costCorrectionDisabledReason
                  || `Editar el último ingreso de stock de ${accessibleFormatName}`}
                value={Number.isFinite(effectiveCost.effective)
                  ? Math.round(effectiveCost.effective)
                  : calculatedCost}
                disabled={costCorrectionDisabled}
                saving={savingCost}
                resetSignal={costResetSignal}
                onEditRequest={() => onEditCalculatedCost?.({ product, variety, format })}
                adjacentAction={stockCreationAction}
              />
              <CostSummary format={format} detailsOnly />
              {costCorrectionDisabledReason && (
                <p id={costCorrectionDescriptionId} className="mt-1 text-[11px] leading-tight text-slate-500">
                  {costCorrectionDisabledReason}
                </p>
              )}
            </div>
          ) : (
            <>
              <InlineQuickPriceInput
                formatId={format.id}
                inputIdPrefix="quick-cost"
                ariaLabel={`Costo manual de ${accessibleFormatName}`}
                fieldLabel="costo"
                allowEmpty
                autoSave={false}
                requireEditActivation
                editButtonAriaLabel={`${hasActiveStandardStockCost
                  ? 'Editar costo estimado'
                  : 'Editar costo manual'} de ${accessibleFormatName}`}
                value={manualEditorValue}
                disabled={costMutationDisabled || recalculatingPrice}
                saving={savingCost}
                resetSignal={costResetSignal}
                placeholder="Automático"
                adjacentAction={stockCreationAction}
                onDraftChange={() => onCostDraftChange?.(format.id)}
                onDirtyChange={(dirty) => onCostDirtyChange?.(format.id, dirty)}
                onCommit={(manualBaseCost) => onSaveCost({
                  product,
                  variety,
                  format,
                  manualBaseCost,
                })}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Costo efectivo: {formatMoney(effectiveCost.effective)}
              </p>
            </>
          )
        ) : <div className="flex items-start gap-1"><CostSummary format={format} />{!offerFormat && stockCreationAction}</div>}
      </td>

      <td className="px-3 py-2 align-top">
        {canEditPricing ? (
          <InlineQuickPriceRuleInput
            formatId={format.id}
            priceRule={priceRuleSelection}
            complexPriceRules={complexPriceRules}
            matchedRange={matchedRange}
            referenceCost={ruleReferenceCost}
            basis={ruleBasis}
            disabled={priceLocked || ruleMutationDisabled || recalculatingPrice}
            saving={savingRule}
            resetSignal={ruleResetSignal}
            modeAriaLabel={`Regla de precio de ${accessibleFormatName}`}
            valueAriaLabel={`Valor de regla de precio de ${accessibleFormatName}`}
            onDraftChange={() => onRuleDraftChange?.(format.id)}
            onDirtyChange={(dirty) => onRuleDirtyChange?.(format.id, dirty)}
            onCommit={(priceRule) => onSaveRule({
              product,
              variety,
              format,
              priceRule,
            })}
          />
        ) : (
          <>
            <QuickPriceRuleBadge title={`${ruleLabel} · ${formatMarginRuleLabel(resolvedSimpleRule, ruleReferenceCost, ruleBasis)}`}>
              {formatMarginRuleLabel(resolvedSimpleRule, ruleReferenceCost, ruleBasis)}
            </QuickPriceRuleBadge>
            {matchedRange && (
              <span className="mt-1 block text-[11px] leading-tight text-blue-700">
                {formatComplexRangeLabel(matchedRange, Number(matchedRange.sortOrder || 0))}
              </span>
            )}
            <QuickPriceRuleEquivalent
              ruleMode={resolvedSimpleRule?.mode}
              margin={getQuickRuleMargin(resolvedSimpleRule, ruleReferenceCost)}
              equivalent={getQuickRuleEquivalent(resolvedSimpleRule, ruleReferenceCost) || (priceRuleSelection?.type === 'complex' ? { unavailable: 'unresolved_rule' } : null)}
              basis={ruleBasis}
            />
          </>
        )}
        {format.priceRuleAssignment?.type === 'main_branch' && (
          <p className="mt-1 text-[11px] text-slate-500">Heredada de la sucursal principal</p>
        )}
      </td>

      <td className="px-3 py-2 align-top">
        {!offerFormat && activeTemporaryPrice.unitPrice && (
          <div className="mb-2 rounded bg-amber-50 px-2 py-1 text-amber-950">
            <p className="font-bold">{formatMoney(activeTemporaryPrice.effectivePrice)} <span className="text-xs">Temporal</span></p>
            <p className="text-xs">{activeTemporaryPrice.unitPrice.startDate} → {activeTemporaryPrice.unitPrice.endDate}</p>
            <p className="mt-1 text-xs text-slate-600">Precio regular / calculado:</p>
          </div>
        )}
        {offerFormat ? (
          <div aria-label={`Vista previa del precio de ${accessibleFormatName}`} aria-live="polite">
            <p className="text-sm font-bold text-slate-900">{offerPreviewPrice == null ? 'Sin precio calculado' : formatMoney(offerPreviewPrice)}</p>
            <p className="mt-1 text-[11px] text-slate-500">{hasPriceRuleSelection(priceRuleSelection)
              ? 'Vista previa según la regla de Oferta.' : 'Selecciona una regla para ver el precio.'}</p>
          </div>
        ) : canUpdateRegularPricing ? (
          <InlineQuickPriceInput
            formatId={format.id}
            value={format.sellingPrice}
            disabled={priceLocked || priceMutationDisabled || recalculatingPrice}
            saving={savingPrice}
            resetSignal={priceResetSignal}
            ariaLabel={`Precio final de ${accessibleFormatName}`}
            onDisplayedValueChange={(price) => {
              setPriceDraft(price);
              onDisplayedDraftChange(format.id, { price });
            }}
            onDraftChange={(draft) => {
              setPriceDraft(draft);
              onPriceDraftChange?.(format.id);
            }}
            onDirtyChange={(dirty) => onPriceDirtyChange?.(format.id, dirty)}
            onCommit={(sellingPrice) => onSavePrice({ product, variety, format, sellingPrice })}
            trailingAction={showRecalculationAction ? {
              ariaLabel: recalculationActionLabel,
              title: `${recalculationActionLabel}: precio pendiente de actualizar`,
              pending: true,
              ariaBusy: recalculatingPrice,
              disabled: recalculationActionDisabled,
              allowWhenInputDisabled: priceLocked,
              onClick: () => onRecalculatePrice?.({ product, variety, format }),
              icon: (
                <FiRefreshCw
                  aria-hidden="true"
                  className={`h-3.5 w-3.5 rounded bg-amber-50 text-amber-700 ${recalculatingPrice ? 'animate-spin' : ''}`}
                />
              ),
            } : null}
          />
        ) : (
          <p className="text-sm font-bold text-slate-900">{formatMoney(format.sellingPrice)}</p>
        )}
        {belowCost && (
          <p
            role="note"
            aria-label={BELOW_COST_DESCRIPTION}
            title={BELOW_COST_DESCRIPTION}
            className="mt-1 flex max-w-full items-center gap-1 text-[11px] font-medium leading-4 text-amber-800"
          >
            <span className="truncate whitespace-nowrap">Bajo costo</span>
          </p>
        )}
        {canUpdate && !offerFormat && (promotionBlockedReason ? (
          <InfoTooltip message={promotionBlockedReason}
            label={`Agregar precio temporal a ${accessibleFormatName}`}
            ariaDisabled disabled={promotionBusy}
            className="mt-1" triggerClassName={`${promotionActionClassName} opacity-60`}>
            {promotionActionText}
          </InfoTooltip>
        ) : (
          <button type="button" disabled={promotionBusy}
            onClick={() => onCreatePromotion?.({ product, variety, format })}
            aria-label={`Agregar precio temporal a ${accessibleFormatName}`}
            className={`mt-1 ${promotionActionClassName}`}>
            {promotionActionText}
          </button>
        ))}
      </td>

      {showAvailability && <td className="px-3 py-2 align-top">
        {!offerFormat && <>
        <div className={availabilityLayoutClasses}>
        {canUpdate ? (
          <AvailabilityButton
            isAvailable={format.isAvailable}
            isConfigured={format.isConfigured}
            saving={savingAvailability}
            disabled={productAvailabilitySaving || availabilityMutationDisabled || recalculatingPrice || format.completion?.isDraft || format.sellingPrice === null}
            ariaLabel={`${format.isAvailable ? 'Marcar no disponible' : 'Marcar disponible'} ${formatName} de ${variety.name}`}
            onClick={() => onToggleAvailability({
              product,
              variety,
              format,
              isAvailable: !format.isAvailable,
            })}
          />
        ) : (
          <span className={`${availabilityBadgeClasses} ${availabilityToneClasses[
            !format.isConfigured ? 'neutral' : format.isAvailable ? 'available' : 'unavailable'
          ]}`}>
            {!format.isConfigured ? 'Sin configurar' : format.isAvailable ? 'Disponible' : 'No disponible'}
          </span>
        )}
        <FormatCompletionNotice compact completion={getQuickAvailabilityCompletion(format)}
          onComplete={onCompleteFormat ? () => onCompleteFormat({ product, variety, format }) : undefined} />
        </div>
        {!format.isConfigured && canUpdateRegularPricing && (
          <p className="mt-1 text-[11px] text-slate-500">Selecciona una regla o ingresa el precio.</p>
        )}
        </>}
      </td>}
      </tr>

      {!hideOfferSummaries && relatedOffers.map((offer) => <QuickOfferSummary key={offer.id} offer={offer}
        now={promotionNow} colSpan={showAvailability ? 5 : 4} canUpdate={canUpdate}
        label={`${variety.name} de ${product.name}`} disabled={promotionMutationDisabled || Boolean(offerBusy[offer.id])}
        error={offerErrors[offer.id]} onEdit={() => onEditOffer?.({ product, variety, format: offer })}
        onEnd={() => onEndOffer?.({ product, variety, format: offer })} />)}

      {!hideOfferSummaries && !offerFormat && <QuickPromotionRows
        format={format}
        formatLabel={accessibleFormatName}
        now={promotionNow}
        canUpdate={canUpdate}
        disabled={promotionMutationDisabled}
        saving={savingPromotion}
        error={promotionError}
        onEdit={(promotion) => onEditPromotion?.({ product, variety, format, promotion })}
        onEnd={(promotion) => onEndPromotion?.({ product, variety, format, promotion })}
      />}

      {error && (
        <tr className="border-t border-red-100 bg-red-50">
          <td colSpan={showAvailability ? 5 : 4} className="px-3 py-2">
            <p role="alert" className="text-xs font-semibold text-red-700">{error}</p>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const QuickPriceCatalog = ({
  stickyToolbarRef,
  onCompleteFormat,
  onReviewPendingFormat,
  pendingOperationsByFormat = {},
  products = [],
  availabilityProducts = products,
  canUpdate = false,
  canUpdateRegularPricing = canUpdate,
  canCorrectStockCosts = false,
  canCreateStock = false,
  savingPrices = {},
  savingAvailability = {},
  savingProducts = {},
  savingSettings = {},
  savingLocks = {},
  recalculationReadyFormats = {},
  recalculationBlockedFormats = {},
  recalculatingFormats = {},
  promotionNow = Date.now(),
  hideOfferSummaries = false,
  savingPromotions = {},
  promotionErrors = {},
  errorsByFormat = {},
  priceResetSignals = {},
  costResetSignals = {},
  ruleResetSignals = {},
  targetFormatId = '',
  onSavePrice,
  onPriceDraftChange,
  onSaveCost,
  onEditCalculatedCost,
  onAddStockInput,
  onSaveRule,
  onCostDraftChange,
  onRuleDraftChange,
  onCostDirtyChange,
  onRuleDirtyChange,
  onPriceDirtyChange,
  onRecalculatePrice,
  onSetPriceLocks,
  onCreatePromotion,
  onEditPromotion,
  onEndPromotion,
  onEditOffer,
  onEndOffer,
  offerBusy = {},
  offerErrors = {},
  onToggleFormatAvailability,
  onRequestProductAvailability,
  selectedFormatIds = [],
  onToggleFormatSelection,
  onSetFormatsSelected,
  mutationDisabled = false,
  lockAllRows = false,
  complexPriceRules = [],
}) => {
  const catalogRef = useUniformRuleBadgeWidth(products.length > 0);
  const showAvailability = products.some((product) => getProductFormats(getCurrentProduct(product)).some((format) => !isOfferFormat(format)));
  const tableHeaderRef = useStickyPriceTableHeader(products.length > 0, stickyToolbarRef);
  const [rowDrafts, setRowDrafts] = useState({});
  const onDisplayedDraftChange = useCallback((id, patch) => {
    setRowDrafts((current) => {
      const next = patch === null ? undefined : { ...current[id], ...patch };
      if (JSON.stringify(current[id]) === JSON.stringify(next)) return current;
      const result = { ...current };
      if (next) result[id] = next;
      else delete result[id];
      return result;
    });
  }, []);
  const warningsById = Object.fromEntries(products.flatMap((product) => getProductFormats(getCurrentProduct(product)))
    .map((format) => [format.id, getQuickPriceWarnings(format, {
      draft: isOfferFormat(format) ? { price: getOfferPreviewPrice(format) } : canUpdate ? rowDrafts[format.id] : undefined, complexPriceRules,
      pendingCount: pendingOperationsByFormat[format.id]?.length, error: errorsByFormat[format.id],
    })]));
  const availabilityWarningsById = Object.fromEntries(products
    .flatMap((product) => getProductFormats(getCurrentProduct(product)))
    .map((format) => [format.id, getQuickAvailabilityWarnings(format)]));
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [expandedVarieties, setExpandedVarieties] = useState({});
  const selectedFormatIdSet = useMemo(() => {
    const requested = new Set(Array.from(selectedFormatIds || []).map(String));
    return new Set(products.flatMap(getProductFormats).filter(format => canUpdateRegularPricing || isOfferFormat(format) ? isSelectableQuickPriceFormat(format) : Boolean(format))
      .map((format) => String(format.id))
      .filter((id) => requested.has(id)));
  }, [products, selectedFormatIds, canUpdateRegularPricing]);
  const selectionEnabled = canUpdate && (
    typeof onToggleFormatSelection === 'function'
    || typeof onSetFormatsSelected === 'function'
  );
  const mutationLock = Boolean(mutationDisabled);
  const priceMutationLocked = mutationLock && (
    typeof mutationDisabled !== 'object' || mutationDisabled.price === true
  );
  const ruleMutationLocked = mutationLock && (
    typeof mutationDisabled !== 'object' || mutationDisabled.rule === true
  );
  const costMutationLocked = mutationLock && (
    typeof mutationDisabled !== 'object' || mutationDisabled.cost === true
  );
  const isSelectable = (format) => canUpdateRegularPricing || isOfferFormat(format)
    ? isSelectableQuickPriceFormat(format) : Boolean(format);
  const setFormatsSelected = (formats, checked) => {
    const selectableFormats = (Array.isArray(formats) ? formats : []).filter(
      isSelectable
    );
    const ids = selectableFormats.map((format) => format.id);
    if (typeof onSetFormatsSelected === 'function') {
      onSetFormatsSelected(ids, checked, selectableFormats);
      return;
    }
    selectableFormats.forEach((format) => {
      const isSelected = selectedFormatIdSet.has(String(format.id));
      if (isSelected !== checked) onToggleFormatSelection?.(format.id, checked, format);
    });
  };
  const toggleFormatSelection = (format, checked) => {
    if (checked && !isSelectable(format)) return;
    if (typeof onToggleFormatSelection === 'function') {
      onToggleFormatSelection(format.id, checked, format);
      return;
    }
    setFormatsSelected([format], checked);
  };
  const availabilityProductMap = useMemo(() => new Map(
    (Array.isArray(availabilityProducts) ? availabilityProducts : [])
      .map((product) => [String(product.id), product])
  ), [availabilityProducts]);
  const targetPath = useMemo(() => {
    if (!targetFormatId) return null;
    for (const product of products) {
      for (const variety of product.varieties) {
        if (variety.formats.some((format) => String(format.id) === String(targetFormatId))) {
          return { productId: product.id, varietyId: variety.id };
        }
      }
    }
    return null;
  }, [products, targetFormatId]);

  useEffect(() => {
    setExpandedProductId((current) => {
      if (current === null || current === undefined) return current;
      return products.some((product) => String(product.id) === String(current))
        ? current
        : null;
    });
  }, [products]);

  useEffect(() => {
    if (!targetPath) return;
    setExpandedProductId(targetPath.productId);
    setExpandedVarieties((current) => ({ ...current, [targetPath.varietyId]: true }));
    const timer = window.setTimeout(() => {
      document.getElementById(`quick-format-${targetFormatId}`)?.scrollIntoView?.({
        block: 'center',
        behavior: 'smooth',
      });
      document.getElementById(`quick-format-${targetFormatId}`)?.focus?.({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [targetFormatId, targetPath]);

  if (!products.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
        No hay productos que coincidan con los filtros.
      </div>
    );
  }

  return (
    <div
      ref={catalogRef}
      role="region"
      tabIndex={0}
      aria-label="Tabla de lista de precios"
      className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
    >
      <table className={`w-full ${showAvailability ? 'min-w-[800px]' : 'min-w-[640px]'} table-fixed text-left text-sm`}>
        <caption className="sr-only">Productos, variedades y formatos de la lista de precios</caption>
        <colgroup>
          <col className="w-[20%]" />
          <col className="w-[17%]" />
          <col />
          <col className="w-[17%]" />
          {showAvailability && <col className="w-44" />}
        </colgroup>
        <thead ref={tableHeaderRef} className="relative z-20 bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
          <tr>
            <th scope="col" className="sticky left-0 z-20 bg-slate-50 px-3 py-2">Producto / variedad / formato</th>
            <th scope="col" className="px-3 py-2">Costo</th>
            <th scope="col" className="px-3 py-2">Regla de precio</th>
            <th scope="col" className="px-3 py-2">Precio final</th>
            {showAvailability && <th scope="col" className="px-3 py-2">Disponibilidad</th>}
          </tr>
        </thead>
        {products.map((product) => {
          const fullProduct = availabilityProductMap.get(String(product.id)) || product;
          const currentFullProduct = getCurrentProduct(fullProduct);
          const visibleProduct = getCurrentProduct(product);
          const visibleProductFormats = getProductFormats(visibleProduct);
          const visibleVarieties = product.varieties.map((variety) => ({ ...variety,
            formats: variety.formats.filter((format) => format.productFormatActive !== false),
          })).filter((variety) => variety.formats.length);
          const productSelection = getFormatSelectionState(visibleProductFormats, selectedFormatIdSet);
          const productSelectableCount = visibleProductFormats.filter(isSelectable).length;
          const productHasSelection = selectionEnabled
            && (productSelection.checked || productSelection.mixed);
          const availabilityFormats = visibleProductFormats.filter((format) => !isOfferFormat(format));
          const configuredFormats = getConfiguredFormats(visibleProduct).filter((format) => !isOfferFormat(format));
          const formatAvailabilitySaving = configuredFormats.some(
            (format) => savingAvailability[format.id]
          );
          const formatRecalculationSaving = configuredFormats.some(
            (format) => recalculatingFormats[format.id]
          );
          const productLockSaving = visibleProductFormats.some((format) => savingLocks[format.id]);
          const productPricingMutationSaving = visibleProductFormats.some((format) => (
            savingPrices[format.id]
            || savingSettings[`${format.id}:rule`]
            || recalculatingFormats[format.id]
          ));
          const summary = getAvailabilitySummary(availabilityFormats, { product: true });
          const productExpanded = expandedProductId !== null
            && expandedProductId !== undefined
            && String(expandedProductId) === String(product.id);
          const nextProductAvailability = summary.tone === 'unavailable';

          return (
            <React.Fragment key={product.id}>
              <tbody>
                <tr
                  id={`quick-product-${product.id}`}
                  aria-selected={selectionEnabled ? productHasSelection : undefined}
                  className="quick-price-row quick-price-product"
                  style={getQuickPriceRowStyle('product', productHasSelection)}
                >
                  <td colSpan={2} className="px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      {selectionEnabled && (
                        <SelectionCheckbox
                          checked={productSelection.checked}
                          mixed={productSelection.mixed}
                          disabled={mutationLock || !productSelectableCount}
                          ariaLabel={`Seleccionar formatos visibles de ${product.name}`}
                          title={!productSelectableCount
                            ? 'No hay formatos desbloqueados para seleccionar.'
                            : undefined}
                          onChange={(event) => setFormatsSelected(visibleProductFormats, event.target.checked)}
                        />
                      )}
                      <button
                        type="button"
                        aria-expanded={productExpanded}
                        aria-controls={product.varieties.map((variety) => `quick-variety-${variety.id}`).join(' ')}
                        onClick={() => {
                          setExpandedVarieties({});
                          setExpandedProductId((current) => (
                            current !== null
                              && current !== undefined
                              && String(current) === String(product.id)
                              ? null
                              : product.id
                          ));
                        }}
                        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <img
                          src={product.image || FALLBACK_IMAGE}
                          alt=""
                          className="h-11 w-11 shrink-0 rounded-lg border border-white/80 bg-white object-cover"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="quick-price-level">Producto</span>
                          <span className="block truncate text-sm font-bold text-slate-950" title={product.name}>{product.name}</span>
                        </span>
                        {productExpanded
                          ? <FiChevronDown aria-hidden="true" className="h-5 w-5 shrink-0 text-slate-600" />
                          : <FiChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-slate-600" />}
                      </button>
                      <PriceLockControl
                        formats={visibleProductFormats}
                        subjectLabel={`los formatos visibles de ${product.name}`}
                        grouped
                        canUpdate={canUpdate && (canUpdateRegularPricing || visibleProductFormats.every(isOfferFormat))}
                        saving={productLockSaving}
                        disabled={mutationLock || productPricingMutationSaving}
                        onChange={(isLocked) => onSetPriceLocks?.({
                          product: visibleProduct,
                          formats: visibleProductFormats,
                          isLocked,
                        })}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <SharedPriceRuleBadge
                      formats={visibleProductFormats}
                      groupLabel={`producto ${product.name}`}
                      complexPriceRules={complexPriceRules}
                    />
                  </td>
                  <td />
                  {showAvailability && <td className="px-3 py-2">
                    {availabilityFormats.length > 0 && <>
                    <div className={availabilityLayoutClasses}>
                    {canUpdate ? (
                      <button
                        type="button"
                        disabled={!configuredFormats.length
                          || savingProducts[product.id]
                          || formatAvailabilitySaving
                          || formatRecalculationSaving
                          || mutationLock}
                        onClick={() => onRequestProductAvailability({
                          product: { ...visibleProduct, varieties: visibleProduct.varieties.map((variety) => ({
                            ...variety, formats: variety.formats.filter((format) => !isOfferFormat(format)),
                          })) },
                          isAvailable: nextProductAvailability,
                          configuredCount: configuredFormats.length,
                        })}
                        aria-label={`${nextProductAvailability ? 'Marcar disponible' : 'Marcar no disponible'} los formatos visibles de ${product.name}`}
                        className={`${availabilityBadgeClasses} transition disabled:cursor-not-allowed disabled:opacity-60 ${availabilityToneClasses[summary.tone]}`}
                      >
                        {savingProducts[product.id] ? 'Actualizando…' : summary.label}
                      </button>
                    ) : (
                      <span className={`${availabilityBadgeClasses} ${availabilityToneClasses[summary.tone]}`}>
                        {summary.label}
                      </span>
                    )}
                    <QuickPriceWarningIndicator subject={`producto ${product.name}`}
                      messages={summarizeQuickPriceWarnings(availabilityFormats, availabilityWarningsById)} />
                    </div>
                    </>}
                  </td>}
                </tr>
              </tbody>

              {productExpanded && visibleVarieties.map((variety) => {
                const fullVariety = currentFullProduct.varieties.find(
                  (entry) => String(entry.id) === String(variety.id)
                ) || variety;
                const varietyAvailabilityFormats = variety.formats.filter((format) => !isOfferFormat(format));
                const varietySummary = getAvailabilitySummary(varietyAvailabilityFormats);
                const varietySelection = getFormatSelectionState(variety.formats, selectedFormatIdSet);
                const varietySelectableCount = variety.formats.filter(isSelectable).length;
                const varietyLockSaving = variety.formats.some(
                  (format) => savingLocks[format.id]
                );
                const varietyPricingMutationSaving = variety.formats.some((format) => (
                  savingPrices[format.id]
                  || savingSettings[`${format.id}:rule`]
                  || recalculatingFormats[format.id]
                ));
                const varietyHasSelection = selectionEnabled
                  && (varietySelection.checked || varietySelection.mixed);
                const varietyExpanded = Boolean(expandedVarieties[variety.id]);
                return (
                  <tbody key={variety.id} id={`quick-variety-${variety.id}`}>
                    <tr
                      aria-selected={selectionEnabled ? varietyHasSelection : undefined}
                      className="quick-price-row quick-price-variety"
                      style={getQuickPriceRowStyle('variety', varietyHasSelection)}
                    >
                      <td colSpan={2} className="py-2 pl-7 pr-3">
                        <div className="flex min-w-0 items-center gap-2">
                          {selectionEnabled && (
                            <SelectionCheckbox
                              checked={varietySelection.checked}
                              mixed={varietySelection.mixed}
                              disabled={mutationLock || !varietySelectableCount}
                              ariaLabel={`Seleccionar formatos visibles de ${variety.name} de ${product.name}`}
                              title={!varietySelectableCount
                                ? 'No hay formatos desbloqueados para seleccionar.'
                                : undefined}
                              onChange={(event) => setFormatsSelected(variety.formats, event.target.checked)}
                            />
                          )}
                          <button
                            type="button"
                            aria-expanded={varietyExpanded}
                            aria-controls={variety.formats.map((format) => `quick-format-${format.id}`).join(' ')}
                            onClick={() => setExpandedVarieties((current) => ({
                              ...current,
                              [variety.id]: !current[variety.id],
                            }))}
                            className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <img
                              src={variety.image || product.image || FALLBACK_IMAGE}
                              alt=""
                              className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 bg-white object-cover"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="quick-price-level" title={`Variedad de ${product.name}`}>Variedad · {product.name}</span>
                              <span className="block truncate text-sm font-semibold text-slate-900" title={variety.name}>{variety.name}</span>
                            </span>
                            {varietyExpanded
                              ? <FiChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-500" />
                              : <FiChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-500" />}
                          </button>
                          <PriceLockControl
                            formats={variety.formats}
                            subjectLabel={`los formatos visibles de ${variety.name} de ${product.name}`}
                            grouped
                            canUpdate={canUpdate && (canUpdateRegularPricing || variety.formats.every(isOfferFormat))}
                            saving={varietyLockSaving}
                            disabled={mutationLock || varietyPricingMutationSaving}
                            onChange={(isLocked) => onSetPriceLocks?.({
                              product: visibleProduct,
                              variety,
                              formats: variety.formats,
                              isLocked,
                            })}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <SharedPriceRuleBadge
                          formats={variety.formats}
                          groupLabel={`variedad ${variety.name} de ${product.name}`}
                          complexPriceRules={complexPriceRules}
                        />
                      </td>
                      <td />
                      {showAvailability && <td className="px-3 py-2">
                        {varietyAvailabilityFormats.length > 0 && <>
                        <div className={availabilityLayoutClasses}>
                        <span className={`${availabilityBadgeClasses} ${availabilityToneClasses[varietySummary.tone]}`}>
                          {varietySummary.label}
                        </span>
                        <QuickPriceWarningIndicator subject={`variedad ${variety.name} de ${product.name}`}
                          messages={summarizeQuickPriceWarnings(varietyAvailabilityFormats, availabilityWarningsById)} />
                        </div>
                        </>}
                      </td>}
                    </tr>

                    {varietyExpanded && <BranchOfferRows formats={fullVariety.formats}
                      colSpan={showAvailability ? 5 : 4} label={`${variety.name} de ${product.name}`} />}
                    {varietyExpanded && variety.formats.map((format) => (
                      <FormatRow
                        key={format.id}
                        warnings={warningsById[format.id] || []}
                        onDisplayedDraftChange={onDisplayedDraftChange}
                        product={product}
                        variety={variety}
                        format={format}
                        relatedOffers={isOfferFormat(format) ? [format] : fullVariety.formats.filter(isOfferFormat)}
                        hideOfferSummaries={hideOfferSummaries}
                        complexPriceRules={complexPriceRules}
                        canUpdate={canUpdate}
                        canUpdateRegularPricing={canUpdateRegularPricing}
                        showAvailability={showAvailability}
                        canCorrectStockCosts={canCorrectStockCosts}
                        canCreateStock={canCreateStock}
                        hasVarietyStockSource={fullVariety.formats.some((entry) => hasStockPurchaseSource(entry.costs))}
                        savingPrice={Boolean(savingPrices[format.id])}
                        savingAvailability={Boolean(savingAvailability[format.id])}
                        productAvailabilitySaving={Boolean(savingProducts[product.id])}
                        savingCost={Boolean(savingSettings[`${format.id}:cost`])}
                        savingRule={Boolean(savingSettings[`${format.id}:rule`])}
                        savingLock={Boolean(savingLocks[format.id])}
                        recalculationReady={Boolean(recalculationReadyFormats[format.id])}
                        recalculationBlocked={Boolean(recalculationBlockedFormats[format.id])}
                        recalculatingPrice={Boolean(recalculatingFormats[format.id])}
                        promotionNow={promotionNow}
                        savingPromotion={Boolean(savingPromotions[format.id])}
                        promotionError={promotionErrors[format.id]}
                        promotionMutationDisabled={mutationLock || Boolean(recalculatingFormats[format.id])}
                        onCompleteFormat={onCompleteFormat}
                        onReviewPendingFormat={onReviewPendingFormat}
                        pendingOperations={pendingOperationsByFormat[format.id]}
                        onCreatePromotion={onCreatePromotion}
                        onEditPromotion={onEditPromotion}
                        onEndPromotion={onEndPromotion}
                        onEditOffer={onEditOffer}
                        onEndOffer={onEndOffer}
                        offerBusy={offerBusy}
                        offerErrors={offerErrors}
                        error={errorsByFormat[format.id]}
                        priceResetSignal={priceResetSignals[format.id]}
                        costResetSignal={costResetSignals[format.id]}
                        ruleResetSignal={ruleResetSignals[format.id]}
                        selectionDisabled={mutationLock
                          || Boolean(recalculatingFormats[format.id])
                          || Boolean(savingLocks[format.id])}
                        costMutationDisabled={costMutationLocked
                          && (lockAllRows || selectedFormatIdSet.has(String(format.id)))}
                        priceMutationDisabled={priceMutationLocked
                          && (lockAllRows || selectedFormatIdSet.has(String(format.id)))}
                        ruleMutationDisabled={ruleMutationLocked
                          && (lockAllRows || selectedFormatIdSet.has(String(format.id)))}
                        availabilityMutationDisabled={mutationLock
                          && (lockAllRows || selectedFormatIdSet.has(String(format.id)))}
                        lockMutationDisabled={mutationLock
                          || Boolean(savingPrices[format.id])
                          || Boolean(savingSettings[`${format.id}:rule`])
                          || Boolean(recalculatingFormats[format.id])}
                        onSavePrice={onSavePrice}
                        onPriceDraftChange={onPriceDraftChange}
                        onSaveCost={onSaveCost}
                        onEditCalculatedCost={onEditCalculatedCost}
                        onAddStockInput={onAddStockInput}
                        onSaveRule={onSaveRule}
                        onCostDraftChange={onCostDraftChange}
                        onRuleDraftChange={onRuleDraftChange}
                        onCostDirtyChange={onCostDirtyChange}
                        onRuleDirtyChange={onRuleDirtyChange}
                        onPriceDirtyChange={onPriceDirtyChange}
                        onRecalculatePrice={onRecalculatePrice}
                        onSetPriceLocks={onSetPriceLocks}
                        onToggleAvailability={onToggleFormatAvailability}
                        selectionEnabled={selectionEnabled}
                        selected={selectionEnabled && selectedFormatIdSet.has(String(format.id))}
                        onToggleSelection={(checked) => toggleFormatSelection(format, checked)}
                        isTarget={Boolean(targetFormatId) && String(format.id) === String(targetFormatId)}
                      />
                    ))}
                  </tbody>
                );
              })}
            </React.Fragment>
          );
        })}
      </table>
    </div>
  );
};

export default QuickPriceCatalog;
